import { useEffect, useState } from "react";
import {
  deleteUser,
  reactivateUser,
  freezeUser,
  unfreezeUser,
} from "../../services/apiService";
import instance from "../../services/endpoint";
import { BiSolidUserCheck, BiSolidUserX } from "react-icons/bi";
import { TbFileDownload } from "react-icons/tb";
import { FaUserCheck } from "react-icons/fa";
import { MdVerifiedUser, MdDoNotDisturbOn, MdDelete } from "react-icons/md";
import { FaSnowflake } from "react-icons/fa6";
import { toast } from "react-toastify";
import { Pagination, Form, Row, Spinner } from "react-bootstrap"; // Import Bootstrap Pagination component
import Loader from "../utilComponents/loader";
import ShimmerLoader from "../utilComponents/shimmer";
import { useAllUsers } from "../../services/hooks/getAdminapis";

const UserManagement = ({ searchQuery }) => {
  const [users, setUsers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1); // Track current page
  const [selectedUser, setSelectedUser] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [depositAmount, setDepositAmount] = useState("");
  const [withdrawPeriod, setWithdrawPeriod] = useState("1"); // Default to Yearly
  const [showReactivateModal, setShowReactivateModal] = useState(false);
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [showAccTypeModal, setShowAccTypeModal] = useState(false);
  const [accType, setAccType] = useState("");
  const [assignLoading, setAssignLoading] = useState(false);
  const [freezeLoading, setFreezeLoading] = useState(false);

  const itemsPerPage = 5; // Items per page

  const { data: allUsers, isLoading, error } = useAllUsers();
  console.log("error in user management", error);

  useEffect(() => {
    if (allUsers && Array.isArray(allUsers)) {
      setUsers(allUsers.reverse());
    }
  }, [allUsers]);

  //  **Filter requests based on searchQuery**
  const filteredRequests = users.filter((request) => {
    const searchLower = searchQuery.toLowerCase();
    const isVerified = request.isEmailVerified ? "verified" : "not checked";

    return (
      request.AccountID.toLowerCase().includes(searchLower) ||
      request.Email.toLowerCase().includes(searchLower) ||
      request.Phone.toLowerCase().includes(searchLower) ||
      request.Account_Type.toLowerCase().includes(searchLower) ||
      request.FullName.toLowerCase().includes(searchLower) ||
      request.ReferralID.toLowerCase().includes(searchLower) ||
      isVerified.includes(searchLower) ||
      (searchLower === "verified" && request.isEmailVerified) ||
      (searchLower === "not checked" && !request.isEmailVerified)
    );
  });

  // Calculate total pages for users`
  const totalPages = Math.ceil(filteredRequests.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = filteredRequests.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

 const downloadCSV = () => {
  const headers = [
    "JoinedDate",
    "AccountID",
    "FullName",
    "Email",
    "EmailVerified",
    "Phone",
    "Account Type",
    "Address",
    "ReferralID",
    "TotalInvestment",
    "TotalReturns",
    "CurrentInvestment",
    "CurrentReturns",
    "Role",
    "Admin ID",
    "User Status",
  ];

  const escapeCSV = (value) => {
    if (value === null || value === undefined) return '""';
    const str = value.toString();
    return `"${str.replace(/"/g, '""')}"`; // Escape quotes and wrap in double quotes
  };

  const rows = filteredRequests.map((user) => [
    escapeCSV(new Date(user.createdAt).toLocaleDateString()),
    escapeCSV(user.AccountID),
    escapeCSV(user.FullName),
    escapeCSV(user.Email),
    escapeCSV(user.isEmailVerified ? "Verified" : "Not Checked"),
    escapeCSV(user.Phone),
    escapeCSV(user.Account_Type),
    escapeCSV(user.Address || "N/A"),
    escapeCSV(user.ReferralID),
    escapeCSV(user.totalInvestment),
    escapeCSV(user.totalReturns),
    escapeCSV(user.currentInvestment),
    escapeCSV(user.currentReturns),
    escapeCSV(user.Role),
    escapeCSV(user.AdminID),
    escapeCSV(user.isDeleted ? "Deleted" : "Active"),
  ]);

  const csvContent = [headers.map(escapeCSV), ...rows]
    .map((row) => row.join(","))
    .join("\n");

  const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" }); // BOM for Excel
  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.setAttribute("href", url);
  link.setAttribute("download", "All_Users.csv");
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

  // =========================API FUNCTIONS=========================
  // Function to deposit amount on behalf of a user====================
  const handleDepositAmount = async (
    AccountID,
    AccountType,
    amount,
    time_period
  ) => {
    if (!amount || !time_period) {
      toast.error("Please enter a valid amount and time period");
      return;
    }
    try {
      setAssignLoading(true);
      const response = await instance.post(
        "/api/v1/deposit/adminSubmitDepositReq",
        { AccountID, AccountType, amount, time_period }
      );
      if (response.status === 200) {
        toast.success("Deposit request submitted successfully");
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Failed to submit deposit request"
      );
    } finally {
      setAssignLoading(false);
    }
  };

  // Function to change Account Type====================
  const handleChangeAccType = async (AccountID, AccountType) => {
    if (!AccountType) {
      toast.error("Please enter a valid Account Type");
      return;
    }
    try {
      setAssignLoading(true);
      const response = await instance.put(
        `/api/v1/admin/control/changeAccountType/${AccountID}`,
        { Account_Type: AccountType }
      );
      if (response.status === 200) {
        toast.success("Account Type changed successfully");
      }
      // Update the user in the state
      setUsers((prevUsers) =>
        prevUsers.map((user) =>
          user.AccountID === AccountID
            ? { ...user, Account_Type: AccountType }
            : user
        )
      );
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Failed to change Account Type"
      );
    } finally {
      setAssignLoading(false);
    }
  };

  return (
    <>
      {freezeLoading && <Loader />}
      {deleteLoading && <Loader />}
      {isLoading && <Loader />}
      <div>
        <h2>User Management</h2>
        <div className="csv">
          <p style={{ color: "green" }}>
            Total Users: {filteredRequests.length}
          </p>
          <button
            onClick={downloadCSV}
            className="downloadBtn"
            style={{ alignSelf: "flex-end" }}>
            <TbFileDownload />
          </button>
        </div>
        <div className="scrollable-table">
          <table>
            <thead style={{ fontSize: "0.8rem" }}>
              <tr>
                <th>JoinedDate</th>
                <th>Account ID</th>
                <th>FullName</th>
                <th>Email</th>
                <th>EmailVerified</th>
                <th>Phone</th>
                <th>Account Type</th>
                <th>Address</th>
                <th>ReferralID</th>
                <th>TotalInvestment</th>
                <th>TotalReturns</th>
                <th>CurrentInvestment</th>
                <th>CurrentReturns</th>
                <th>Role</th>
                <th>KYC-Status</th>
                <th>Admin ID</th>
                <th>Account Freez</th>
                <th>User Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {Array.isArray(currentItems) && currentItems.length > 0 ? (
                currentItems.map((user) => (
                  <tr key={user.AccountID}>
                    <td>{new Date(user.createdAt).toLocaleDateString()}</td>
                    <td>{user.AccountID}</td>
                    <td>{user.FullName}</td>
                    <td>{user.Email}</td>
                    <td>
                      {user.isEmailVerified ? (
                        <MdVerifiedUser style={{ color: "green" }} />
                      ) : (
                        <MdDoNotDisturbOn style={{ color: "red" }} />
                      )}
                    </td>
                    <td>{user.Phone}</td>
                    <td>{user.Account_Type}</td>
                    <td>{user.Address ? user.Address : "N/A"}</td>
                    <td>{user.ReferralID}</td>
                    <td>₹{user.totalInvestment}</td>
                    <td>₹{user.totalReturns}</td>
                    <td style={{ color: "green" }}>
                      <b>₹{user.currentInvestment}</b>
                    </td>
                    <td style={{ color: "green" }}>
                      <b>₹{user.currentReturns}</b>
                    </td>
                    <td>{user.Role}</td>
                    <td>
                      <span
                        style={{
                          color:
                            user.KYC_Status === "Pending" ? "red" : "green",
                        }}>
                        {user.KYC_Status}
                      </span>
                    </td>
                    <td>{user.AdminID} </td>
                    <td>
                      {user.isFreezed ? (
                        <span style={{ color: "blue" }}>
                          <FaSnowflake /> Freezed
                        </span>
                      ) : (
                        <span style={{ color: "green" }}>
                          <BiSolidUserCheck /> Active
                        </span>
                      )}
                    </td>
                    <td>
                      {user.isDeleted ? (
                        <span style={{ color: "red" }}>
                          <BiSolidUserX /> Deleted
                        </span>
                      ) : (
                        <span style={{ color: "green" }}>
                          <BiSolidUserCheck /> Active
                        </span>
                      )}
                    </td>

                    <td
                      style={{
                        width: "200px",
                        minWidth: "200px",
                        maxWidth: "200px",
                      }}>
                      <div
                        className="d-flex justify-content-space-between align-items-space-between"
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          width: "100%",
                        }}>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            gap: "0.5rem",
                            flexWrap: "wrap",
                            // marginRight: "0.5rem",
                            marginBottom: "0.5rem",
                          }}>
                          {user.isDeleted ? (
                            <button
                              style={{
                                background: "green",
                                color: "#fff",
                                border: "none",
                                borderRadius: "4px",
                                padding: "2px 8px",
                                fontSize: "0.8rem",
                                cursor: "pointer",
                              }}
                              onClick={() => {
                                setSelectedUser(user); // sets the full user object
                                setShowReactivateModal(true); // show modal
                              }}>
                              Reactivate
                            </button>
                          ) : (
                            <button
                              style={{
                                background: "black",
                                color: "#fff",
                                border: "none",
                                borderRadius: "4px",
                                padding: "2px 8px",
                                fontSize: "0.8rem",
                                cursor: "pointer",
                              }}
                              onClick={() => {
                                setSelectedUser(user); // sets the full user object
                                setShowDeleteModal(true); // show modal
                              }}>
                              Delete
                            </button>
                          )}
                        </div>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            gap: "0.5rem",
                            flexWrap: "wrap",
                          }}>
                          <button
                            style={{
                              background: "blueviolet",
                              color: "#fff",
                              border: "none",
                              borderRadius: "4px",
                              padding: "2px 8px",
                              fontSize: "0.8rem",
                              cursor: "pointer",
                            }}
                            onClick={() => {
                              setSelectedUser(user); // sets the full user object
                              setShowDepositModal(true); // show modal
                            }}>
                            Deposit
                          </button>
                          <button
                            style={{
                              background: "purple",
                              color: "#fff",
                              border: "none",
                              borderRadius: "4px",
                              padding: "2px 8px",
                              fontSize: "0.7rem",
                              cursor: "pointer",
                            }}
                            onClick={() => {
                              setSelectedUser(user);
                              setShowAccTypeModal(true);
                            }}>
                            Account Type
                          </button>
                          {user.isFreezed ? (
                            <button
                              style={{
                                background: "green",
                                color: "#fff",
                                border: "none",
                                borderRadius: "4px",
                                padding: "2px 8px",
                                fontSize: "0.7rem",
                                cursor: "pointer",
                              }}
                              onClick={async () => {
                                setFreezeLoading(true);
                                await unfreezeUser(user.AccountID);
                                setUsers((prevUsers) =>
                                  prevUsers.map((u) =>
                                    u.AccountID === user.AccountID
                                      ? { ...u, isFreezed: false }
                                      : u
                                  )
                                );
                                toast.success("User unfreezed successfully");
                                setFreezeLoading(false);
                                setSelectedUser(null);
                              }}>
                              Un Freeze
                            </button>
                          ) : (
                            <button
                              style={{
                                background: "blue",
                                color: "#fff",
                                border: "none",
                                borderRadius: "4px",
                                padding: "2px 8px",
                                fontSize: "0.7rem",
                                cursor: "pointer",
                              }}
                              onClick={async () => {
                                setFreezeLoading(true);
                                await freezeUser(user.AccountID);
                                setUsers((prevUsers) =>
                                  prevUsers.map((u) =>
                                    u.AccountID === user.AccountID
                                      ? { ...u, isFreezed: true }
                                      : u
                                  )
                                );
                                toast.success("User freezed successfully");
                                setFreezeLoading(false);
                                setSelectedUser(null);
                              }}>
                              Freeze
                            </button>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                ))
              ) : error ? (
                <tr>
                  <td colSpan="17" style={{ color: "red" }}>
                    {error?.response?.data?.message ||
                      error?.message ||
                      "Failed to fetch users"}
                  </td>
                </tr>
              ) : isLoading ? (
                <tr>
                  <td colSpan="17">
                    <ShimmerLoader width="100%" height="30px" />
                  </td>
                </tr>
              ) : (
                <tr>
                  <td colSpan="17">No users found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="d-flex justify-content-center mt-3">
          <Pagination>
            {[...Array(totalPages)].map((_, pageIndex) => (
              <Pagination.Item
                key={pageIndex}
                active={currentPage === pageIndex + 1}
                onClick={() => handlePageChange(pageIndex + 1)}>
                {pageIndex + 1}
              </Pagination.Item>
            ))}
          </Pagination>
        </div>
      </div>

      {/*===================================================Modals==================================================== */}
      {/* Modal for change Account Type */}
      {showAccTypeModal && selectedUser && (
        <div
          className="modal-backdrop"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}>
          <div
            className="modal-content"
            style={{
              backgroundColor: "#fff",
              padding: "2rem",
              borderRadius: "8px",
              width: "400px",
              maxWidth: "90%",
              boxShadow: "0 0 15px rgba(0,0,0,0.2)",
              position: "relative",
            }}>
            <h4>Change Account Type for {selectedUser.FullName}</h4>
            <p>
              <strong>Account ID:</strong> {selectedUser.AccountID}
            </p>
            <p>
              <strong>Account_Type:</strong>{" "}
              {selectedUser.Account_Type || "None"}
            </p>

            <Form.Group className="mb-2" controlId="formAccountType">
              <Form.Label
                style={{
                  fontSize: "0.8rem",
                  margin: "0 0 1px -15rem",
                  color: "white",
                }}>
                Account Type
              </Form.Label>
              <Form.Select
                name="Account_Type"
                value={accType}
                onChange={(e) => setAccType(e.target.value)}
                required>
                <option value="">Select Account Type</option>
                <option value="Classic">Classic</option>
                <option value="Premium">Premium</option>
                <option value="Business">Business</option>
                <option value="Enterprise">Enterprise</option>
              </Form.Select>
            </Form.Group>

            {/* Deposit Button */}
            <button
              style={{
                padding: "8px 12px",
                backgroundColor: "#27ae60",
                color: "#fff",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
              }}
              onClick={async () => {
                await handleChangeAccType(selectedUser.AccountID, accType);
                setShowAccTypeModal(false);
                setAccType("");
                setSelectedUser(null);
              }}>
              {assignLoading ? (
                <Spinner animation="border" size="sm" />
              ) : (
                "Change Account Type"
              )}
            </button>

            <button
              onClick={() => setShowAccTypeModal(false)}
              style={{
                backgroundColor: "red",
                color: "white",
                position: "absolute",
                top: "10px",
                right: "10px",
                border: "none",
                fontSize: "1.2rem",
                cursor: "pointer",
                borderRadius: "50%",
                width: "30px",
                height: "30px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 0,
              }}>
              x
            </button>
          </div>
        </div>
      )}

      {/* Modal for depositing amount */}
      {showDepositModal && selectedUser && (
        <div
          className="modal-backdrop"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}>
          <div
            className="modal-content"
            style={{
              backgroundColor: "#fff",
              padding: "2rem",
              borderRadius: "8px",
              width: "400px",
              maxWidth: "90%",
              boxShadow: "0 0 15px rgba(0,0,0,0.2)",
              position: "relative",
            }}>
            <h4>Deposit Amount on behalf of {selectedUser.FullName}</h4>
            <p>
              <strong>Account ID:</strong> {selectedUser.AccountID}
            </p>
            <p>
              <strong>Account_Type:</strong>{" "}
              {selectedUser.Account_Type || "None"}
            </p>

            <hr />
            {/* amount input */}
            <label className="form-label text-start mb-0">Deposit Amount</label>
            <input
              type="text"
              placeholder="Enter Amount"
              value={depositAmount}
              onChange={(e) => setDepositAmount(e.target.value)}
              style={{
                width: "100%",
                padding: "8px",
                margin: "10px 0",
                borderRadius: "4px",
                border: "1px solid #ccc",
              }}
            />
            {/* select time period */}
            <Row className="mb-3">
              <Form.Group className="text-start">
                <Form.Label className="text-start mb-2">Withdraw Period</Form.Label>
                <Form.Select
                  name="withdrawPeriod"
                  value={withdrawPeriod}
                  onChange={(e) => setWithdrawPeriod(e.target.value)}
                  required>
                  <option value="1">Yearly</option>
                  <option value="2">Half Yearly</option>
                  <option value="4">Quarterly</option>
                </Form.Select>
              </Form.Group>
            </Row>

            {/* Deposit Button */}
            <button
              style={{
                padding: "8px 12px",
                backgroundColor: "#27ae60",
                color: "#fff",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
              }}
              onClick={async () => {
                await handleDepositAmount(
                  selectedUser.AccountID,
                  selectedUser.Account_Type,
                  depositAmount,
                  withdrawPeriod
                );
                setShowDepositModal(false);
                setDepositAmount("");
                setWithdrawPeriod("");
              }}>
              {assignLoading ? (
                <Spinner animation="border" size="sm" />
              ) : (
                "Deposit"
              )}
            </button>

            <button
              onClick={() => setShowDepositModal(false)}
              style={{
                backgroundColor: "red",
                color: "white",
                position: "absolute",
                top: "10px",
                right: "10px",
                border: "none",
                fontSize: "1.2rem",
                cursor: "pointer",
                borderRadius: "50%",
                width: "30px",
                height: "30px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 0,
              }}>
              x
            </button>
          </div>
        </div>
      )}

      {/* delete confirmation modal */}
      {showDeleteModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}>
          <div
            style={{
              backgroundColor: "#fff",
              padding: "20px",
              borderRadius: "8px",
              textAlign: "center",
              minWidth: "300px",
            }}>
            <MdDelete style={{ color: "red", fontSize: "5rem" }} />
            <h5>Are you sure you want to delete this user?</h5>
            <div
              style={{
                marginTop: "1rem",
                display: "flex",
                justifyContent: "space-around",
              }}>
              <button
                style={{
                  backgroundColor: "red",
                  color: "white",
                  padding: "6px 12px",
                  border: "none",
                  borderRadius: "4px",
                }}
                onClick={async () => {
                  if (selectedUser && selectedUser.AccountID) {
                    try {
                      setDeleteLoading(true);
                      await deleteUser(selectedUser.AccountID);
                      toast.success("User deleted successfully");

                      // Optionally refresh the users list
                      setUsers((prev) =>
                        prev.map((u) =>
                          u.AccountID === selectedUser.AccountID
                            ? { ...u, isDeleted: true }
                            : u
                        )
                      );
                    } catch (error) {
                      toast.error(
                        error?.response?.data?.message ||
                          "Failed to delete user"
                      );
                      console.error("Delete error:", error);
                    } finally {
                      setDeleteLoading(false);
                    }
                  }
                  setShowDeleteModal(false);
                  setSelectedUser(null);
                }}>
                Yes
              </button>

              <button
                style={{
                  backgroundColor: "gray",
                  color: "white",
                  padding: "6px 12px",
                  border: "none",
                  borderRadius: "4px",
                }}
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedUser(null); // reset
                }}>
                No
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reactivate confirmation modal */}
      {showReactivateModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}>
          <div
            style={{
              backgroundColor: "#fff",
              padding: "20px",
              borderRadius: "8px",
              textAlign: "center",
              minWidth: "300px",
            }}>
            <FaUserCheck style={{ color: "green", fontSize: "5rem" }} />
            <h5>Are you sure you want to reactivate this user?</h5>
            <div
              style={{
                marginTop: "1rem",
                display: "flex",
                justifyContent: "space-around",
              }}>
              <button
                style={{
                  backgroundColor: "green",
                  color: "white",
                  padding: "6px 12px",
                  border: "none",
                  borderRadius: "4px",
                }}
                onClick={async () => {
                  if (selectedUser && selectedUser.AccountID) {
                    try {
                      setDeleteLoading(true);
                      await reactivateUser(selectedUser.AccountID);
                      toast.success("User reactivated successfully");

                      // Optionally refresh the users list
                      setUsers((prev) =>
                        prev.map((u) =>
                          u.AccountID === selectedUser.AccountID
                            ? { ...u, isDeleted: false }
                            : u
                        )
                      );
                    } catch (error) {
                      toast.error(
                        error?.response?.data?.message ||
                          "Failed to reactivate user"
                      );
                      console.error("Reactivate error:", error);
                    } finally {
                      setDeleteLoading(false);
                    }
                  }
                  setShowReactivateModal(false);
                  setSelectedUser(null);
                }}>
                Yes
              </button>

              <button
                style={{
                  backgroundColor: "gray",
                  color: "white",
                  padding: "6px 12px",
                  border: "none",
                  borderRadius: "4px",
                }}
                onClick={() => {
                  setShowReactivateModal(false);
                  setSelectedUser(null); // reset
                }}>
                No
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UserManagement;
